class FeaturedCollectionProduct extends HTMLElement {
  constructor() {
    super();
    this.selectedVariantId = null;
    this.selectedVariantTitle = null;
    this.initialized = false;
    this._globalClickHandler = null;
  }
  
  disconnectedCallback() {
    if (this._globalClickHandler) {
      document.removeEventListener('click', this._globalClickHandler, true);
    }
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.initSwatches();
      this.initQuickAdd();
      this.hidePriceCurrency();
      this.initBadgeColor();
    });
  }
  
  initBadgeColor() {
    const badge = this.querySelector('.card-badge');
    if (badge && badge.dataset.badgeColor) {
      const color = badge.dataset.badgeColor;
      // Convert hex to RGB for rgba
      const hex = color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      badge.style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.75)`;
    }
  }

  hidePriceCurrency() {
    const priceElement = this.querySelector('.featured-collection-price');
    if (priceElement) {
      const priceItems = priceElement.querySelectorAll('.price-item, .money');
      priceItems.forEach(item => {
        item.textContent = item.textContent.replace(/\s*USD\s*/gi, '').trim();
      });
    }
  }

  initSwatches() {
    const swatchesContainer = this.querySelector('.variant-swatches');
    if (!swatchesContainer) return;

    const swatches = swatchesContainer.querySelectorAll('.variant-swatch');
    if (swatches.length === 0) return;

    // Find the first image in the media container (the main product image)
    const mediaContainer = this.querySelector('.media.media--hover-effect');
    // Try multiple selectors to find the product image
    const productImage = mediaContainer 
      ? mediaContainer.querySelector('img.product-card-image') || mediaContainer.querySelector('img:first-child')
      : this.querySelector('.product-card-image') || this.querySelector('.card__media img:first-child');
    const secondaryImage = mediaContainer ? mediaContainer.querySelector('img:nth-child(2)') : null;
    
    // Store original image src for reset
    if (productImage) {
      this.originalImageSrc = productImage.src;
      this.originalImageSrcset = productImage.srcset;
    }

    // Handle swatch selection
    const handleSwatchClick = (e, swatch) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Remove selected class from all swatches
      swatches.forEach(s => s.classList.remove('selected'));
      // Add selected class to clicked swatch
      swatch.classList.add('selected');

      // Update selected variant
      this.selectedVariantId = swatch.dataset.variantId;
      this.selectedVariantTitle = swatch.dataset.variantTitle;
      const imageUrl = swatch.dataset.imageUrl;

      // Update product image if available
      if (productImage && imageUrl) {
        // Generate responsive srcset
        const baseUrl = imageUrl.split('?')[0];
        const sizes = [165, 360, 533, 720, 940, 1066];
        const srcsetParts = sizes.map(size => {
          return `${baseUrl}?width=${size} ${size}w`;
        });
        
        // Update image source
        productImage.srcset = srcsetParts.join(', ');
        productImage.src = imageUrl;
        
        // Force image reload by creating a new image and replacing
        const newImage = new Image();
        newImage.src = imageUrl;
        newImage.srcset = srcsetParts.join(', ');
        newImage.onload = () => {
          productImage.src = imageUrl;
          productImage.srcset = srcsetParts.join(', ');
        };
        
        // Hide secondary image if it exists (to prevent hover from showing it)
        if (secondaryImage) {
          secondaryImage.style.display = 'none';
          secondaryImage.style.opacity = '0';
        }
        
        // Add class to disable hover effect on the media container and card
        if (mediaContainer) {
          mediaContainer.classList.add('variant-selected');
        }
        this.classList.add('variant-selected');
      }
      
      return false;
    };

    swatches.forEach((swatch) => {
      // Force pointer events on the swatch
      swatch.style.pointerEvents = 'auto';
      swatch.style.cursor = 'pointer';
      swatch.style.position = 'relative';
      swatch.style.zIndex = '1001';
      
      // Add mousedown handler to prevent navigation
      swatch.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        // Immediately trigger the click handler
        handleSwatchClick(e, swatch);
        return false;
      }, true);
      
      // Add touchstart for mobile
      swatch.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        // Trigger click handler
        handleSwatchClick(e, swatch);
        return false;
      }, true);
      
      // Add click handler for selection - use capture phase (runs first)
      swatch.addEventListener('click', (e) => {
        handleSwatchClick(e, swatch);
      }, true);
      
      // Also add as bubble phase handler
      swatch.addEventListener('click', (e) => {
        handleSwatchClick(e, swatch);
      }, false);
      
      // Also add as direct handler in case event listeners don't work
      swatch.onclick = (e) => {
        handleSwatchClick(e, swatch);
      };
    });
    
    // Also prevent any parent click handlers from interfering
    const cardContent = this.querySelector('.card__content');
    if (cardContent) {
      cardContent.addEventListener('click', (e) => {
        const clickedSwatch = e.target.closest('.variant-swatch');
        const clickedSwatches = e.target.closest('.variant-swatches');
        if (clickedSwatch || clickedSwatches) {
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      }, true);
    }
    
    // Also add a global click handler on the document to catch any missed clicks
    const globalClickHandler = (e) => {
      const clickedSwatch = e.target.closest('.variant-swatch');
      if (clickedSwatch && this.contains(clickedSwatch)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleSwatchClick(e, clickedSwatch);
        return false;
      }
    };
    document.addEventListener('click', globalClickHandler, true);
    this._globalClickHandler = globalClickHandler;
  }

  initQuickAdd() {
    const quickAddButton = this.querySelector('.quick-add-button');
    if (!quickAddButton) return;

    // Prevent clicks on quick add container from navigating
    const quickAddContainer = this.querySelector('.card__quick-add');
    if (quickAddContainer) {
      quickAddContainer.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    quickAddButton.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (!this.selectedVariantId) return;

      const formData = {
        items: [{
          id: parseInt(this.selectedVariantId),
          quantity: 1
        }]
      };

      try {
        quickAddButton.disabled = true;
        quickAddButton.textContent = 'ADDING...';

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          quickAddButton.textContent = 'ADDED!';
          
          // Dispatch cart update event
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          
          // Update cart count
          fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
              const cartCount = cart.item_count;
              const cartIcon = document.querySelector('#cart-icon-bubble .cart-count-bubble');
              if (cartIcon) {
                cartIcon.textContent = cartCount;
              }
            });

          setTimeout(() => {
            quickAddButton.disabled = false;
            quickAddButton.textContent = `QUICK ADD ${this.selectedVariantTitle}`;
          }, 2000);
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        quickAddButton.disabled = false;
        quickAddButton.textContent = 'ERROR - TRY AGAIN';
      }
    });
  }
}

if (!customElements.get('featured-collection-product')) {
  customElements.define('featured-collection-product', FeaturedCollectionProduct);
}

// Initialize any existing cards that might not have been initialized
function initializeFeaturedCollectionCards() {
  document.querySelectorAll('.featured-collection-product:not([data-initialized])').forEach(card => {
    if (card instanceof FeaturedCollectionProduct) {
      card.setAttribute('data-initialized', 'true');
    } else {
      // If it's not already a custom element, it should be wrapped
      const wrapper = document.createElement('featured-collection-product');
      Array.from(card.attributes).forEach(attr => {
        wrapper.setAttribute(attr.name, attr.value);
      });
      wrapper.innerHTML = card.innerHTML;
      card.parentNode.replaceChild(wrapper, card);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFeaturedCollectionCards);
} else {
  initializeFeaturedCollectionCards();
}

// Also initialize after dynamic content loads
document.addEventListener('shopify:section:load', initializeFeaturedCollectionCards);

// Prevent navigation from card content clicks on swatches/button areas
document.addEventListener('click', (e) => {
  const swatch = e.target.closest('.variant-swatch');
  const quickAdd = e.target.closest('.card__quick-add, .quick-add-button');
  
  if (swatch || quickAdd) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    return false;
  }
}, true);

